import React, { useMemo, useEffect } from 'react';
import styles from './ActionDetailsModal.module.scss';
import { useTranslation } from 'react-i18next';
import CloseChannelIcon from '@jwp/ott-theme/assets/icons/Close_channel.svg?react';
import { ActionDetailsModalProps } from './ActionDetailsModal.types';

const BADGE_CLASS_MAP: Record<string, string> = {
  copyright_legal: styles.copyright,
  sexual_content: styles.sexualContent,
  spam_scam: styles.spamScam,
  hate_harassment: styles.hateHarassment,
  misinformation: styles.misinformation,
  violence: styles.violence,
};

const CONTENT_HEIGHT_CLASS_MAP: Record<string, string> = {
  WARN_CREATOR: styles.warnCreatorContent,
  REMOVE_CONTENT: styles.removeContentContent,
  SUSPEND_CHANNEL: styles.suspendChannelContent,
  DELETE_CHANNEL: styles.deleteChannelContent,
};

const ActionDetailsModal: React.FC<ActionDetailsModalProps> = ({ details, onClose }) => {
  if (!details) return null;

  const { t } = useTranslation(['user', 'video', 'common', 'upload']);
  const {
    actionType,
    actionLabel,
    warningTypes = [],
    message,
    suspensionPeriodLabel,
    durationDays,
    takenOn,
  } = details;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const formattedDate = useMemo(() => {
    if (!takenOn) return '—';
    const date = new Date(takenOn);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [takenOn]);

  const contentSizeClass = CONTENT_HEIGHT_CLASS_MAP[actionType] || styles.warnCreatorContent;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('user:report_details.action_details')}</h2>
          {onClose && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close modal"
            >
              <CloseChannelIcon />
            </button>
          )}
        </div>

        {/* Content Container with Dynamic Heights */}
        <div className={`${styles.modalContentWrapper} ${contentSizeClass}`}>
          {/* Action Taken */}
          <div className={styles.group}>
            <div className={styles.label}>{t('user:report_details.action_taken')}</div>
            <div className={styles.value}>{actionLabel || '—'}</div>
          </div>

          {/* Dynamic Section 1: Warning Types (WARN_CREATOR) */}
          {actionType === 'WARN_CREATOR' && warningTypes && warningTypes.length > 0 && (
            <div className={styles.group}>
              <div className={styles.label}>{t('user:report_details.warning_Type')}</div>
              <div className={styles.badgeGroup}>
                {warningTypes.map((item) => (
                  <span
                    key={item.rrId || item.rrCode || item.rrLabel}
                    className={`${styles.badge} ${BADGE_CLASS_MAP[item.rrCode || ''] || ''}`}
                  >
                    {item.rrLabel}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Dynamic Section 2: Suspension Period (SUSPEND_CHANNEL) */}
          {actionType === 'SUSPEND_CHANNEL' && (suspensionPeriodLabel || durationDays) && (
            <div className={styles.group}>
              <div className={styles.label}>{t('user:report_details.suspension_period')}</div>
              <div className={styles.value}>
                {suspensionPeriodLabel || `${durationDays} ${t('common:days')}`}
              </div>
            </div>
          )}

          {/* Reason / Warning Message */}
          <div className={styles.group}>
            <div className={styles.label}>
              {actionType === 'WARN_CREATOR'
                ? t('user:report_details.warning_message')
                : t('user:report_details.reason')}
            </div>
            <div className={styles.value}>{message || '—'}</div>
          </div>

          {/* Taken On */}
          <div className={styles.group}>
            <div className={styles.label}>{t('user:report_details.taken_on')}</div>
            <div className={styles.value}>{formattedDate}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionDetailsModal;